import { useCallback, useRef, useState } from "react";
import { DocumentType } from "@api/graphql/generated/generated-types";
import { useCourieStore } from "@src/common/lib/store";
import { DOCUMENT_TYPE_LABELS } from "@src/driverDocuments/constants/documentTypeLabels";
import { useDriverComplianceCheck } from "./useDriverComplianceCheck";

export function useDriverComplianceGate() {
  const { checkCompliance, loading } = useDriverComplianceCheck();
  const { showToast } = useCourieStore();
  const [warningTypes, setWarningTypes] = useState<DocumentType[]>([]);
  const [showWarning, setShowWarning] = useState(false);
  const onProceedRef = useRef<(() => void) | null>(null);

  const check = useCallback(
    (driverId: string, orderIds: string[], onPass: () => void) => {
      onProceedRef.current = onPass;

      if (orderIds.length === 0) {
        onPass();
        return;
      }

      checkCompliance(driverId, orderIds)
        .then((statuses) => {
          const allMissingRequired = Array.from(
            new Set(statuses.flatMap((s) => s.missingRequired)),
          );
          const allMissingWarned = Array.from(
            new Set(statuses.flatMap((s) => s.missingWarned)),
          );

          if (allMissingRequired.length > 0) {
            const labels = allMissingRequired
              .map((t) => DOCUMENT_TYPE_LABELS[t] ?? t)
              .join(", ");
            showToast({
              message: `Cannot assign driver. Missing required certifications: ${labels}`,
              type: "error",
            });
            return;
          }

          if (allMissingWarned.length > 0) {
            setWarningTypes(allMissingWarned);
            setShowWarning(true);
            return;
          }

          onPass();
        })
        .catch(() => {
          showToast({
            message:
              "Unable to verify driver certifications. Please try again.",
            type: "error",
          });
        });
    },
    [checkCompliance],
  );

  const handleProceed = useCallback(() => {
    setShowWarning(false);
    onProceedRef.current?.();
  }, []);

  const handleCloseWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  return {
    check,
    loading,
    showWarning,
    warningTypes,
    closeWarning: handleCloseWarning,
    proceedFromWarning: handleProceed,
  };
}
